import { clamp } from '../core/math/scalar'

export interface Comandi {
  /** assi d'assetto in [-1, 1] */
  pitch: number
  yaw: number
  roll: number
  throttle: number
}

export interface InputHandler {
  readonly comandi: Comandi
  /** true se il pilota sta chiedendo assetto in questo istante */
  chiedeAssetto(): boolean
  /** fa salire e scendere gli assi con una rampa: va chiamato ogni frame */
  aggiorna(dt: number): void
  destroy(): void
}

export interface InputAzioni {
  stadio(): void
  paracadute(): void
  cambiaCamera(): void
  cambiaAutopilota(): void
  progrado(): void
  puntaAlNodo(): void
  warp(delta: number): void
  mappa(): void
}

const ASSI: Record<string, [keyof Comandi, number]> = {
  KeyW: ['pitch', 1], KeyS: ['pitch', -1],
  KeyA: ['yaw', -1], KeyD: ['yaw', 1],
  KeyQ: ['roll', -1], KeyE: ['roll', 1],
}

/**
 * Tastiera → comandi. Gli assi non sono a scatto: salgono e scendono con una
 * rampa, cosi una pressione breve da una correzione piccola invece di
 * sbattere il gimbal a fondo corsa.
 */
export function createInput(azioni: InputAzioni): InputHandler {
  const premuti = new Set<string>()
  const comandi: Comandi = { pitch: 0, yaw: 0, roll: 0, throttle: 0 }

  const giu = (e: KeyboardEvent): void => {
    if (e.repeat) { premuti.add(e.code); return }
    premuti.add(e.code)
    switch (e.code) {
      case 'Space': e.preventDefault(); azioni.stadio(); break
      case 'KeyC': azioni.cambiaCamera(); break
      case 'KeyT': azioni.cambiaAutopilota(); break
      case 'KeyF': azioni.progrado(); break
      case 'KeyG': azioni.puntaAlNodo(); break
      case 'KeyP': azioni.paracadute(); break
      case 'KeyZ': comandi.throttle = 1; break
      case 'KeyX': comandi.throttle = 0; break
      case 'KeyM': azioni.mappa(); break
      case 'Comma': azioni.warp(-1); break
      case 'Period': azioni.warp(1); break
    }
  }
  const su = (e: KeyboardEvent): void => { premuti.delete(e.code) }

  window.addEventListener('keydown', giu)
  window.addEventListener('keyup', su)

  const rampa = (attuale: number, voluto: number, dt: number): number => {
    const velocita = voluto === 0 ? 6 : 4
    return clamp(attuale + (voluto - attuale) * Math.min(1, velocita * dt), -1, 1)
  }

  return {
    comandi,
    chiedeAssetto: () => ['KeyW', 'KeyS', 'KeyA', 'KeyD', 'KeyQ', 'KeyE'].some((k) => premuti.has(k)),
    destroy() {
      window.removeEventListener('keydown', giu)
      window.removeEventListener('keyup', su)
    },
    aggiorna(dt: number): void {
        const voluto: Record<string, number> = { pitch: 0, yaw: 0, roll: 0 }
        for (const codice of premuti) {
          const a = ASSI[codice]
          if (a) voluto[a[0] as string] = a[1]
        }
        comandi.pitch = rampa(comandi.pitch, voluto.pitch!, dt)
        comandi.yaw = rampa(comandi.yaw, voluto.yaw!, dt)
        comandi.roll = rampa(comandi.roll, voluto.roll!, dt)

        if (premuti.has('ShiftLeft') || premuti.has('ShiftRight')) {
          comandi.throttle = clamp(comandi.throttle + dt * 0.75, 0, 1)
        }
        if (premuti.has('ControlLeft') || premuti.has('ControlRight')) {
          comandi.throttle = clamp(comandi.throttle - dt * 0.75, 0, 1)
        }
    },
  }
}
